import { useState, useEffect, useRef, useCallback } from 'react';

// Хук: становится true, когда элемент попадает (или почти попадает) во вьюпорт.
// Срабатывает один раз, дальше отписывается от наблюдения.
const useInView = () => {
    const ref = useRef(null);
    const [inView, setInView] = useState(false);

    useEffect(() => {
        const node = ref.current;
        if (!node) return;

        if (!('IntersectionObserver' in window)) {
            setInView(true);
            return;
        }

        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                setInView(true);
                observer.disconnect();
            }
        }, {
            rootMargin: '50px', // грузим только когда блок реально почти на экране,
                                 // а не заранее на весь следующий экран — иначе несколько
                                 // тяжёлых видео стартуют одновременно и душат друг друга
            threshold: 0.15,
        });

        observer.observe(node);
        return () => observer.disconnect();
    }, []);

    return [ref, inView];
};

// Ленивая гифка/видео: до попадания во вьюпорт показывает лёгкий постер,
// после — грузит видео (webm/mp4), если оно задано, иначе обычную картинку (gif/png/jpg).
const LazyMedia = ({ image, className = '' }) => {
    const [ref, inView] = useInView();
    const [loaded, setLoaded] = useState(false);

    const hasVideo = Boolean(image.srcWebm || image.srcMp4);
    const placeholder = image.poster || image.placeholder;

    return (
        <div ref={ref} className="relative w-full">
            {placeholder && (
                <img
                    src={placeholder}
                    alt=""
                    aria-hidden="true"
                    className={`absolute inset-0 w-full h-full object-contain transition-opacity duration-300 ${loaded ? 'opacity-0' : 'opacity-100'} ${className}`}
                />
            )}

            {inView && hasVideo && (
                <video
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload="metadata"
                    poster={placeholder}
                    onLoadedData={() => setLoaded(true)}
                    className={`w-full h-auto transition-opacity duration-300 ${loaded ? 'opacity-100' : 'opacity-0'} ${className}`}
                >
                    {image.srcWebm && <source src={image.srcWebm} type="video/webm" />}
                    {image.srcMp4 && <source src={image.srcMp4} type="video/mp4" />}
                </video>
            )}

            {inView && !hasVideo && (
                <img
                    src={image.src}
                    alt={image.alt}
                    onLoad={() => setLoaded(true)}
                    className={`w-full h-auto transition-opacity duration-300 ${loaded ? 'opacity-100' : 'opacity-0'} ${className}`}
                />
            )}
        </div>
    );
};

export const HeroImage = ({
    src,
    alt,
    bgColor = 'bg-gray-100',
    paddingMobile = '4',
    paddingDesktop = '6'
}) => {
    return (
        <div className="w-full mb-8">
            <div
                className={`
                    ${bgColor}
                    rounded-lg
                    flex items-center justify-center
                    px-${paddingMobile} py-${paddingMobile}
                    lg:px-${paddingDesktop} lg:py-${paddingDesktop}
                `}
            >
                <img
                    src={src}
                    alt={alt}
                    className="w-full h-auto object-contain rounded-lg"
                />
            </div>
        </div>
    );
};

export const TextBlock = ({
    content,
    color = '#474746',
    fontSize = 'text-lg',
    alignment = 'left',
    marginBottom = 'mb-8'
}) => {
    const alignmentClasses = {
        left: 'text-left',
        center: 'text-center',
        right: 'text-right'
    };

    return (
        <div className={`prose max-w-none ${marginBottom}`}>
            <p
                className={`${fontSize} leading-relaxed ${alignmentClasses[alignment]}`}
                style={{ color }}
            >
                {Array.isArray(content)
                    ? content.map((item, index) => {
                          if (item.type === "link") {
                              return (
                                  <a
                                      key={index}
                                      href={item.href}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      style={{
                                          color: item.color || "#6fb2ff"
                                      }}
                                      className="hover:underline"
                                  >
                                      {item.text}
                                  </a>
                              );
                          }

                          return (
                              <span key={index}>
                                  {item.text}
                              </span>
                          );
                      })
                    : content.split("\n").map((line, index, arr) => (
                          <span key={index}>
                              {line}
                              {index < arr.length - 1 && <br />}
                          </span>
                      ))}
            </p>
        </div>
    );
};

export const ImageGallery = ({ images }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [hoveredImage, setHoveredImage] = useState(null);

    const galleryRef = useRef(null);
    const fullscreenRef = useRef(null);

    const touchStartX = useRef(null);
    const touchStartY = useRef(null);

    /*
     * =========================================
     * КОЛЕСО МЫШИ — ОСНОВНАЯ ГАЛЕРЕЯ
     * =========================================
     *
     * Колесо мыши превращается в горизонтальную
     * прокрутку через scrollLeft.
     */
    useEffect(() => {
        const gallery = galleryRef.current;

        if (!gallery) return;

        const handleWheel = (e) => {
            /*
             * Полностью блокируем вертикальную
             * прокрутку страницы.
             */
            e.preventDefault();
            e.stopPropagation();

            /*
             * Используем вертикальное колесо мыши
             * как горизонтальную прокрутку.
             */
            const delta =
                Math.abs(e.deltaY) > Math.abs(e.deltaX)
                    ? e.deltaY
                    : e.deltaX;

            if (Math.abs(delta) < 1) {
                return;
            }

            /*
             * Плавно двигаем реальную горизонтальную
             * ленту.
             */
            gallery.scrollLeft += delta;
        };

        gallery.addEventListener("wheel", handleWheel, {
            passive: false,
        });

        return () => {
            gallery.removeEventListener("wheel", handleWheel);
        };
    }, []);

    /*
     * =========================================
     * TOUCH / СВАЙП
     * =========================================
     */
    const handleTouchStart = (e) => {
        const touch = e.touches[0];

        touchStartX.current = touch.clientX;
        touchStartY.current = touch.clientY;
    };

    const handleTouchMove = (e) => {
        if (touchStartX.current === null) {
            return;
        }

        const touch = e.touches[0];

        const deltaX =
            touchStartX.current - touch.clientX;

        const deltaY =
            touchStartY.current - touch.clientY;

        /*
         * Если движение преимущественно горизонтальное,
         * прокручиваем галерею вручную.
         */
        if (Math.abs(deltaX) > Math.abs(deltaY)) {
            e.preventDefault();

            galleryRef.current.scrollLeft += deltaX;

            touchStartX.current = touch.clientX;
            touchStartY.current = touch.clientY;
        }
    };

    const handleTouchEnd = () => {
        touchStartX.current = null;
        touchStartY.current = null;
    };

    /*
     * =========================================
     * ОПРЕДЕЛЕНИЕ ТЕКУЩЕГО ИЗОБРАЖЕНИЯ
     * =========================================
     */
    const handleScroll = () => {
        const gallery = galleryRef.current;

        if (!gallery) return;

        const items =
            gallery.querySelectorAll(
                "[data-gallery-image]"
            );

        if (!items.length) return;

        let closestIndex = 0;
        let closestDistance = Infinity;

        const galleryCenter =
            gallery.scrollLeft +
            gallery.clientWidth / 2;

        items.forEach((item, index) => {
            const itemCenter =
                item.offsetLeft +
                item.offsetWidth / 2;

            const distance = Math.abs(
                itemCenter - galleryCenter
            );

            if (distance < closestDistance) {
                closestDistance = distance;
                closestIndex = index;
            }
        });

        setCurrentIndex(closestIndex);
    };

    /*
     * =========================================
     * ПРОКРУТКА ОСНОВНОЙ ГАЛЕРЕИ СТРЕЛКАМИ
     * =========================================
     */
    const scrollGalleryBy = (amount) => {
        const gallery = galleryRef.current;

        if (!gallery) return;

        gallery.scrollBy({
            left: amount,
            behavior: "smooth",
        });
    };

    /*
     * =========================================
     * НАВИГАЦИЯ ВПЕРЁД / НАЗАД (FULLSCREEN)
     * =========================================
     */
    const goToPrev = useCallback(() => {
        setCurrentIndex((prev) =>
            prev === 0 ? images.length - 1 : prev - 1
        );
    }, [images]);

    const goToNext = useCallback(() => {
        setCurrentIndex((prev) =>
            prev === images.length - 1 ? 0 : prev + 1
        );
    }, [images]);

    /*
     * =========================================
     * FULLSCREEN — КОЛЕСО МЫШИ
     * =========================================
     */
    useEffect(() => {
        const fullscreen = fullscreenRef.current;

        if (!fullscreen || !isFullscreen) {
            return;
        }

        const handleWheel = (e) => {
            e.preventDefault();
            e.stopPropagation();

            const delta =
                Math.abs(e.deltaY) > Math.abs(e.deltaX)
                    ? e.deltaY
                    : e.deltaX;

            if (Math.abs(delta) < 1) {
                return;
            }

            if (delta > 0) {
                goToNext();
            } else {
                goToPrev();
            }
        };

        fullscreen.addEventListener(
            "wheel",
            handleWheel,
            { passive: false }
        );

        return () => {
            fullscreen.removeEventListener(
                "wheel",
                handleWheel
            );
        };
    }, [isFullscreen, goToNext, goToPrev]);

    /*
     * =========================================
     * FULLSCREEN — КЛАВИАТУРА (СТРЕЛКИ / ESC)
     * =========================================
     */
    useEffect(() => {
        if (!isFullscreen) return;

        const handleKeyDown = (e) => {
            if (e.key === "ArrowRight") {
                goToNext();
            } else if (e.key === "ArrowLeft") {
                goToPrev();
            } else if (e.key === "Escape") {
                setIsFullscreen(false);
            }
        };

        window.addEventListener("keydown", handleKeyDown);

        return () => {
            window.removeEventListener(
                "keydown",
                handleKeyDown
            );
        };
    }, [isFullscreen, goToNext, goToPrev]);

    /*
     * =========================================
     * ОТКРЫТЬ FULLSCREEN
     * =========================================
     */
    const openFullscreen = (index) => {
        setCurrentIndex(index);
        setIsFullscreen(true);
        setHoveredImage(null);
    };

    /*
     * =========================================
     * ЗАКРЫТЬ FULLSCREEN
     * =========================================
     */
    const closeFullscreen = () => {
        setIsFullscreen(false);
    };

    /*
     * =========================================
     * EMPTY STATE
     *
     * ВАЖНО:
     * Он находится после всех Hooks.
     * =========================================
     */
    if (!images || images.length === 0) {
        return null;
    }

    const hasMultipleImages = images.length > 1;

    return (
        <>
            {/* =========================================
                ОСНОВНАЯ ГАЛЕРЕЯ
            ========================================== */}
            <div className="w-full mb-4 flex items-center gap-2">
                {/* Стрелка влево (снаружи серого блока) */}
                {hasMultipleImages && (
                    <button
                        onClick={() =>
                            scrollGalleryBy(-240)
                        }
                        className="
                            flex-shrink-0
                            w-9
                            h-9
                            flex
                            items-center
                            justify-center
                            rounded-full
                            bg-gray-100
                            text-gray-600
                            text-xl
                            hover:bg-gray-200
                            transition-colors
                        "
                        aria-label="Прокрутить влево"
                    >
                        ‹
                    </button>
                )}

                <div
                    ref={galleryRef}
                    onScroll={handleScroll}
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                    className="
                        relative
                        flex-1
                        min-w-0
                        bg-gray-100
                        rounded-lg
                        overflow-x-auto
                        overflow-y-visible
                        px-2
                        py-5
                        select-none
                        scrollbar-hide
                    "
                    style={{
                        overscrollBehaviorX: "contain",
                        overscrollBehaviorY: "none",
                        scrollbarWidth: "none",
                    }}
                >
                    {/* Горизонтальная лента */}
                    <div
                        className="
                            flex
                            items-center
                            gap-4
                            w-max
                            min-w-full
                            px-8
                        "
                    >
                        {images.map((image, index) => (
                            <div
                                key={index}
                                data-gallery-image
                                className="
                                    relative
                                    flex-shrink-0
                                    h-[100px]
                                    md:h-[140px]
                                    lg:h-[160px]
                                    cursor-pointer
                                "
                                onClick={() =>
                                    openFullscreen(index)
                                }
                                onMouseEnter={(e) => {
                                    const rect =
                                        e.currentTarget.getBoundingClientRect();

                                    setHoveredImage({
                                        image,
                                        rect,
                                    });
                                }}
                                onMouseLeave={() => {
                                    setHoveredImage(null);
                                }}
                            >
                                <img
                                    src={image.src}
                                    alt={image.alt || ""}
                                    draggable={false}
                                    className="
                                        block
                                        h-full
                                        w-auto
                                        max-w-none
                                        object-contain
                                        rounded-lg
                                        ring-2
                                        ring-transparent
                                        brightness-100
                                        transition-all
                                        duration-200
                                        ease-out
                                        hover:ring-white
                                        hover:brightness-125
                                    "
                                />
                            </div>
                        ))}
                    </div>
                </div>

                {/* Стрелка вправо (снаружи серого блока) */}
                {hasMultipleImages && (
                    <button
                        onClick={() =>
                            scrollGalleryBy(240)
                        }
                        className="
                            flex-shrink-0
                            w-9
                            h-9
                            flex
                            items-center
                            justify-center
                            rounded-full
                            bg-gray-100
                            text-gray-600
                            text-xl
                            hover:bg-gray-200
                            transition-colors
                        "
                        aria-label="Прокрутить вправо"
                    >
                        ›
                    </button>
                )}
            </div>

            {/* =========================================
                HOVER-ПРЕВЬЮ
            ========================================== */}
            {hoveredImage && (
                <div
                    className="
                        fixed
                        z-[9999]
                        pointer-events-none
                    "
                    style={{
                        left: hoveredImage.rect.left,
                        top: hoveredImage.rect.top,
                        width: hoveredImage.rect.width,
                        height: hoveredImage.rect.height,
                    }}
                >
                    <img
                        src={hoveredImage.image.src}
                        alt={hoveredImage.image.alt || ""}
                        draggable={false}
                        className="
                            absolute
                            left-1/2
                            top-1/2
                            -translate-x-1/2
                            -translate-y-1/2
                            max-w-none
                            h-full
                            w-auto
                            object-contain
                            rounded-lg
                            scale-[1.45]
                            bg-white
                            shadow-[0_0_0_4px_rgba(255,255,255,0.95),0_20px_60px_rgba(0,0,0,0.45)]
                            transition-transform
                            duration-200
                            ease-out
                        "
                    />
                </div>
            )}

            {/* =========================================
                FULLSCREEN
            ========================================== */}
            {isFullscreen && (
                <div
                    ref={fullscreenRef}
                    className="
                        fixed
                        inset-0
                        z-[10000]
                        bg-black/90
                        flex
                        items-center
                        justify-center
                        p-4
                        md:p-8
                        select-none
                    "
                    onClick={closeFullscreen}
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                >
                    {/* Закрыть */}
                    <button
                        onClick={closeFullscreen}
                        className="
                            absolute
                            top-4
                            right-4
                            z-[10001]
                            w-12
                            h-12
                            flex
                            items-center
                            justify-center
                            rounded-full
                            bg-white/20
                            text-white
                            text-2xl
                            hover:bg-white/30
                            transition-colors
                        "
                        aria-label="Закрыть"
                    >
                        ×
                    </button>

                    {/* Стрелка влево */}
                    {hasMultipleImages && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                goToPrev();
                            }}
                            className="
                                absolute
                                left-2
                                md:left-6
                                top-1/2
                                -translate-y-1/2
                                z-[10001]
                                w-12
                                h-12
                                flex
                                items-center
                                justify-center
                                rounded-full
                                bg-white/20
                                text-white
                                text-2xl
                                hover:bg-white/30
                                transition-colors
                            "
                            aria-label="Предыдущее изображение"
                        >
                            ‹
                        </button>
                    )}

                    {/* Большое изображение */}
                    <img
                        src={images[currentIndex].src}
                        alt={
                            images[currentIndex].alt || ""
                        }
                        onClick={(e) =>
                            e.stopPropagation()
                        }
                        draggable={false}
                        className="
                            max-w-[90vw]
                            max-h-[90vh]
                            w-auto
                            h-auto
                            object-contain
                            rounded-lg
                            shadow-2xl
                        "
                    />

                    {/* Стрелка вправо */}
                    {hasMultipleImages && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                goToNext();
                            }}
                            className="
                                absolute
                                right-2
                                md:right-6
                                top-1/2
                                -translate-y-1/2
                                z-[10001]
                                w-12
                                h-12
                                flex
                                items-center
                                justify-center
                                rounded-full
                                bg-white/20
                                text-white
                                text-2xl
                                hover:bg-white/30
                                transition-colors
                            "
                            aria-label="Следующее изображение"
                        >
                            ›
                        </button>
                    )}

                    {/* Номер */}
                    <div
                        className="
                            absolute
                            bottom-4
                            left-1/2
                            -translate-x-1/2
                            px-3
                            py-1
                            rounded-full
                            bg-white/20
                            text-white
                            text-sm
                            backdrop-blur-sm
                        "
                    >
                        {currentIndex + 1} /{" "}
                        {images.length}
                    </div>
                </div>
            )}
        </>
    );
};

// 660x360 - gifs
export const TwoColumn = ({ columns }) => {
    const imagePosition = columns.find(col => col.type === 'image')?.position;
    
    return (
        <div className="flex flex-col lg:flex-row mb-8 gap-0 lg:gap-12">
            {columns.map((column, index) => {
                switch (column.type) {
                    case 'text':
                        const alignmentClasses = {
                            left: 'text-left',
                            center: 'text-center',
                            right: 'text-right'
                        };
                        
                        const textPaddingClass = imagePosition === 'left' ? 'pl-4 lg:pl-6' : '';
                        
                        if (column.lines && Array.isArray(column.lines)) {
                            return (
                                <div key={index} className={`${column.width} ${textPaddingClass}`}>
                                    <div className="prose max-w-none">
                                        {column.lines.map((line, lineIndex) => (
                                            <p 
                                                key={lineIndex}
                                                className={`${line.fontSize || 'text-lg'} leading-relaxed ${alignmentClasses[line.alignment || 'left']} mb-2`}
                                                style={{ 
                                                    color: line.color || '#474746',
                                                    marginLeft: line.marginLeft || '0px',
                                                    marginRight: line.marginRight || '0px',
                                                    marginTop: line.marginTop || '0px',
                                                    marginBottom: line.marginBottom || '8px'
                                                }}
                                            >
                                                {line.content}
                                            </p>
                                        ))}
                                    </div>
                                </div>
                            );
                        }
                        
                        return (
                            <div key={index} className={`${column.width} ${textPaddingClass}`}>
                                <TextBlock 
                                    content={column.content}
                                    color={column.color}
                                    fontSize={column.fontSize}
                                    alignment={column.alignment}
                                />
                            </div>
                        );
                    
                        
                    case 'image':
                        const bgColor = column.bgColor || 'bg-gray-100';
                        const paddingMobile = column.paddingMobile || '4';
                        const paddingDesktop = column.paddingDesktop || '6';
                        
                        return (
                            <div key={index} className={`${column.width}
                                ${bgColor} rounded-lg flex items-center justify-center
                                px-${paddingMobile} py-${paddingMobile}
                                lg:px-${paddingDesktop} lg:py-${paddingDesktop}`}>
                                {column.images.map((image, imgIndex) => (
                                    <LazyMedia
                                        key={imgIndex}
                                        image={image}
                                        className="object-contain rounded-lg"
                                    />
                                ))}
                            </div>
                        );
                    
                    default:
                        return null;
                }
            })}
        </div>
    );
};

export const LocalVideo = ({ src, poster, title, autoplay = true }) => {
    return (
        <div className="w-full mb-8">
            <video 
                className="w-full rounded-lg shadow-lg"
                controls
                poster={poster}
                autoPlay={autoplay}
                muted={autoplay} 
                loop={autoplay}
            >
                <source src={src} type="video/mp4" />
                Your browser does not support the video tag.
            </video>
            {title && (
                <p className="text-sm text-gray-600 mt-2 text-center">{title}</p>
            )}
        </div>
    );
};

const MobileFallback = ({ mobileFallback }) => (
    <div className="w-full mb-8 relative">
        {mobileFallback ? (
            <>
                <img
                    src={mobileFallback.poster || mobileFallback.src}
                    alt={mobileFallback.alt}
                    loading="lazy"
                    className="w-full rounded-lg"
                />
                <div className="absolute top-3 right-3 bg-black/60 text-white text-xs px-2 py-1 rounded-md backdrop-blur-sm">
                    WebGL · open on desktop
                </div>
            </>
        ) : (
            <div className="w-full rounded-lg bg-gray-100 flex items-center justify-center py-12 text-sm text-gray-400">
                WebGL · open on desktop
            </div>
        )}
    </div>
);

const WebGLPlayer = ({ buildPath, buildName, compression = "", width = "100%", height = "600px" }) => {
    const containerRef = useRef(null);
    const unityInstanceRef = useRef(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!buildPath || !buildName) {
            setError('Build path or name not specified');
            setLoading(false);
            return;
        }

        // 'br' / 'gz' для сжатых билдов Unity -> .data.br, .framework.js.br, .wasm.br
        const suffix = compression ? `.${compression}` : '';
        let script = null;
        let isMounted = true;
        
        const loadUnity = async () => {
            try {
                script = document.createElement('script');
                const loaderUrl = `${buildPath}/Build/${buildName}.loader.js`;
                script.src = `${loaderUrl}?v=${Date.now()}`;
                script.async = true;
                
                script.onload = async () => {
                    if (isMounted) {
                        if (typeof window.createUnityInstance !== 'undefined') {
                            try {
                                const unityInstance = await window.createUnityInstance(containerRef.current, {
                                dataUrl: `${buildPath}/Build/${buildName}.data${suffix}`,
                                frameworkUrl: `${buildPath}/Build/${buildName}.framework.js${suffix}`,
                                codeUrl: `${buildPath}/Build/${buildName}.wasm${suffix}`,
                            });

                            unityInstanceRef.current = unityInstance;

                            if (isMounted) setLoading(false);
                            } catch (err) {
                                if (isMounted) {
                                    setError(`Failed to initialize Unity: ${err.message}`);
                                    setLoading(false);
                                }
                            }
                        } else {
                            setError('Unity loader not available');
                            setLoading(false);
                        }
                    }
                };
                
                script.onerror = () => {
                    if (isMounted) {
                        setError(`Failed to load Unity loader from: ${loaderUrl}`);
                        setLoading(false);
                    }
                };

                document.body.appendChild(script);
            } catch (err) {
                if (isMounted) {
                    setError(`Error loading Unity: ${err.message}`);
                    setLoading(false);
                }
            }
        };

        loadUnity();

        return () => {
            isMounted = false;

            const unityInstance = unityInstanceRef.current;

            if (unityInstance) {
                try {
                    if (typeof unityInstance.Quit === 'function') { 
                        unityInstance.Quit();
                    }
                } catch (e) {
                    console.error('Error quitting Unity:', e);
                }
            }

            if (script && script.parentNode) {
                try {
                    document.body.removeChild(script);
                } catch (e) {
                    console.error('Error removing script:', e);
                }
            }
        };
    }, [buildPath, buildName, compression]);

    if (error) {
        return (
            <div className="w-full mb-8 p-8 bg-red-50 rounded-lg border border-red-200">
                <p className="text-red-600 font-semibold">WebGL Load Error:</p>
                <p className="text-red-500 text-sm mt-2">{error}</p>
                <p className="text-gray-600 text-xs mt-4">
                    Please make sure the Unity WebGL build is available at: <br />
                    <code className="bg-gray-100 px-2 py-1 rounded">{buildPath}/Build/{buildName}.loader.js</code>
                </p>
            </div>
        );
    }

    return (
        <div className="w-full mb-8 relative">
            {loading && (
                <div
                    className="absolute inset-0 flex items-center justify-center bg-gray-100 rounded-lg z-10"
                    style={{ minHeight: height }}
                >
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
                        <p className="text-gray-600">Loading Unity application...</p>
                    </div>
                </div>
            )}
            <canvas
                ref={containerRef}
                id="unity-canvas"
                style={{ width, height, display: loading ? 'none' : 'block' }}
                className="rounded-lg bg-black"
            />
        </div>
    );
};

export const WebGLEmbed = ({ buildPath, buildName, compression, width, height, mobileFallback }) => {
    const isMobile = window.innerWidth < 1024;
    
    if (isMobile) {
        return <MobileFallback mobileFallback={mobileFallback} />;
    }
    
    return (
        <WebGLPlayer
            buildPath={buildPath}
            buildName={buildName}
            compression={compression}
            width={width}
            height={height}
        />
    );
};

export const GitHubLink = ({ url, text, alignment = 'left' }) => {
    const alignmentClasses = {
        left: 'justify-start',
        center: 'justify-center',
        right: 'justify-end'
    };
    
    return (
        <div className={`mb-8 flex ${alignmentClasses[alignment]}`}>
            <a 
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 hover:bg-gray-800 text-white rounded-lg transition-colors duration-200"
            >
                <svg 
                    className="w-5 h-5" 
                    fill="currentColor" 
                    viewBox="0 0 24 24"
                >
                    <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                </svg>
                <span>{text || "View on GitHub"}</span>
            </a>
        </div>
    );
};

export const ItchIoLink = ({ url, text, alignment = 'left' }) => {
    const alignmentClasses = {
        left: 'justify-start',
        center: 'justify-center',
        right: 'justify-end'
    };
    
    return (
        <div className={`mb-8 flex ${alignmentClasses[alignment]}`}>
            <a 
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors duration-200"
            >
                <svg 
                    className="w-5 h-5" 
                    fill="currentColor" 
                    viewBox="0 0 245.371 220.736"
                >
                    <path d="M31.99 1.365C21.287 7.72.2 31.945 0 38.298v10.516C0 62.144 12.46 73.86 23.773 73.86c13.584 0 24.902-11.258 24.903-24.62 0 13.362 10.93 24.62 24.515 24.62 13.586 0 24.165-11.258 24.165-24.62 0 13.362 11.622 24.62 25.207 24.62h.246c13.586 0 25.208-11.258 25.208-24.62 0 13.362 10.58 24.62 24.164 24.62 13.585 0 24.515-11.258 24.515-24.62 0 13.362 11.32 24.62 24.903 24.62 11.313 0 23.773-11.714 23.773-25.046V38.298c-.2-6.354-21.287-30.58-31.988-36.933C180.118.197 157.056-.005 122.685 0c-34.37.003-81.228.54-90.697 1.365zm65.194 66.217a28.025 28.025 0 0 1-4.78 6.155c-5.128 5.014-12.157 8.122-19.906 8.122a28.482 28.482 0 0 1-19.948-8.126c-1.858-1.82-3.27-3.766-4.563-6.032l-.006.004c-1.292 2.27-3.092 4.215-4.954 6.037a28.5 28.5 0 0 1-19.948 8.12c-.934 0-1.906-.258-2.692-.528-1.092 11.372-1.553 22.24-1.716 30.164l-.002.045c-.02 4.024-.04 7.333-.06 11.93.21 23.86-2.363 77.334 10.52 90.473 19.964 4.655 56.7 6.775 93.555 6.788h.006c36.854-.013 73.59-2.133 93.554-6.788 12.883-13.14 10.31-66.614 10.52-90.474-.022-4.596-.04-7.905-.06-11.93l-.003-.045c-.162-7.926-.623-18.793-1.715-30.165-.786.27-1.757.528-2.692.528a28.5 28.5 0 0 1-19.948-8.12c-1.862-1.822-3.662-3.766-4.955-6.037l-.006-.004c-1.294 2.266-2.705 4.213-4.563 6.032a28.48 28.48 0 0 1-19.947 8.125c-7.748 0-14.778-3.11-19.906-8.123a28.025 28.025 0 0 1-4.78-6.155 27.99 27.99 0 0 1-4.736 6.155 28.49 28.49 0 0 1-19.95 8.124c-.27 0-.54-.012-.81-.02h-.007c-.27.008-.54.02-.813.02a28.49 28.49 0 0 1-19.95-8.123 27.992 27.992 0 0 1-4.736-6.155zm-20.486 26.49l-.002.01h.015c8.113.017 15.32 0 24.25 9.746 7.028-.737 14.372-1.105 21.722-1.094h.006c7.35-.01 14.694.357 21.723 1.094 8.93-9.747 16.137-9.73 24.25-9.746h.014l-.002-.01c3.833 0 19.166 0 29.85 30.007L210 165.244c8.504 30.624-2.723 31.373-16.727 31.4-20.768-.773-32.267-15.855-32.267-30.935-11.496 1.884-24.907 2.826-38.318 2.827h-.006c-13.412 0-26.823-.943-38.318-2.827 0 15.08-11.5 30.162-32.267 30.935-14.004-.027-25.23-.775-16.726-31.4L46.85 124.08C57.534 94.073 72.867 94.073 76.7 94.073z"/>
                </svg>
                <span>{text || "Play on itch.io"}</span>
            </a>
        </div>
    );
};

export const ProjectContentRenderer = ({ content }) => {
    switch (content.type) {
        case 'hero-image':
            return <HeroImage src={content.src} alt={content.alt} />;
        
        case 'text':
            return <TextBlock 
                content={content.content}
                color={content.color}
                fontSize={content.fontSize}
                alignment={content.alignment}
                marginBottom={content.marginBottom} 
            />;
        
        case 'image-gallery':
            return <ImageGallery images={content.images} />;

        case 'two-column':
            return <TwoColumn columns={content.columns} />;
        
        case 'local-video':
            return <LocalVideo src={content.src} poster={content.poster} title={content.title} />;
        
        case 'webgl':
            return <WebGLEmbed 
                buildPath={content.buildPath} 
                buildName={content.buildName}
                compression={content.compression}
                width={content.width}
                height={content.height}
                mobileFallback={content.mobileFallback} 
            />;
        case 'github-link':
            return <GitHubLink url={content.url} text={content.text} alignment={content.alignment} />;
        case 'itchio-link':
            return <ItchIoLink url={content.url} text={content.text} alignment={content.alignment} />;
        
        default:
            return null;
    }
};